import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { ContractStatusFilterEnum } from '../../../../common/enums/invoicing.enum';
import { ChildContractDto, NextContractNumberDto } from '../../../../common/interfaces/invoicing.interface';
import { getContracts, getNextContractNumber, setContractNumber, setPayer } from '../../../../api/invoicing.api';

import { palette } from '../../../../theme';
export const DEFAULT_PATTERN = 'UG-{godina}/{broj}';
/** The same rule the backend validates: literal characters a contract number may hold, plus the two placeholders. */
const PATTERN_SHAPE = /^(?:[\w\-/.]|\{(?:godina|year|broj|number)\})+$/;
const NUMBER_TOKEN = /\{(?:broj|number)\}/g;
const YEAR_TOKEN = /\{(?:godina|year)\}/g;
/** The list endpoint's largest page. */
const PAGE_SIZE = 100;

export const isValidPattern = (pattern: string): boolean => PATTERN_SHAPE.test(pattern) && (pattern.match(NUMBER_TOKEN) ?? []).length === 1;

export const fillPattern = (pattern: string, year: number, value: number, digits: number): string =>
  pattern.replace(YEAR_TOKEN, String(year)).replace(NUMBER_TOKEN, String(value).padStart(digits, '0'));

interface AssignContractNumbersDialogProps {
  kindergartenId: string;
  kindergartenName?: string;
  onClose: () => void;
  /** Called once the run has written anything, so the list behind can refresh. */
  onDone: () => void;
}

interface RunReport {
  assigned: number;
  total: number;
  payersSet: number;
  failures: { child: string; reason: string }[];
}

/**
 * Numbers every child that has none, in one go.
 *
 * The numbers are worked out here and shown before anything is written, then set one call
 * at a time through the same endpoint the panel uses - which keeps its uniqueness check, and
 * means one refused number is reported rather than taking the rest down with it.
 *
 * A child without a group enrolment cannot hold a contract (it belongs to the enrolment),
 * so it stays in the preview, marked as skipped, instead of silently missing from it.
 */
const AssignContractNumbersDialog: React.FC<AssignContractNumbersDialogProps> = ({ kindergartenId, kindergartenName, onClose, onDone }) => {
  const { t } = useTranslation();
  const [children, setChildren] = useState<ChildContractDto[] | null>(null);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [info, setInfo] = useState<NextContractNumberDto | null>(null);
  const [firstNumber, setFirstNumber] = useState('');
  const [firstTouched, setFirstTouched] = useState(false);
  const [setPayers, setSetPayers] = useState(true);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [report, setReport] = useState<RunReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all: ChildContractDto[] = [];
        for (let page = 1; ; page += 1) {
          const result = await getContracts(kindergartenId, { status: ContractStatusFilterEnum.NUMBER, page, perPage: PAGE_SIZE });
          all.push(...(result.results ?? []));
          if (page >= (result.paginationInfo?.totalPages ?? 1)) break;
        }
        if (!cancelled) setChildren(all);
      } catch {
        if (!cancelled) setFailedToLoad(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kindergartenId]);

  const patternValid = isValidPattern(pattern);

  useEffect(() => {
    if (!patternValid) return;
    let cancelled = false;
    // typed one character at a time; ask once the admin pauses
    const timer = setTimeout(() => {
      getNextContractNumber(kindergartenId, pattern)
        .then(result => {
          if (cancelled || !result.results) return;
          setInfo(result.results);
          if (!firstTouched) setFirstNumber(String(result.results.nextNumber));
        })
        .catch(() => undefined);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [kindergartenId, pattern, patternValid, firstTouched]);

  const first = /^\d+$/.test(firstNumber) ? Number(firstNumber) : NaN;
  const firstValid = Number.isInteger(first) && first > 0;
  const year = info?.year ?? new Date().getFullYear();
  const digits = info?.digits ?? 3;

  const preview = useMemo(() => {
    let next = first;
    return (children ?? []).map(child => {
      if (!child.membershipId) return { child, number: null as string | null };
      const number = patternValid && firstValid ? fillPattern(pattern, year, next, digits) : null;
      next += 1;
      return { child, number };
    });
  }, [children, pattern, patternValid, first, firstValid, year, digits]);

  const assignable = preview.filter(row => row.child.membershipId);
  // only the obvious case is offered: with two parents, who pays is a decision
  const payerCandidates = (children ?? []).filter(child => !child.payerFullName && child.parents.length === 1);

  const run = async () => {
    const failures: RunReport['failures'] = [];
    const reasonOf = (error: any) => error?.response?.data?.message ?? t('contracts.assign.unknownReason');
    const payerTargets = setPayers ? payerCandidates : [];
    const total = assignable.length + payerTargets.length;
    let assigned = 0;
    let payersSet = 0;
    setProgress({ done: 0, total });

    for (let index = 0; index < assignable.length; index += 1) {
      const row = assignable[index];
      try {
        await setContractNumber(kindergartenId, row.child.childId, row.number!, row.child.membershipId!);
        assigned += 1;
      } catch (error) {
        failures.push({ child: row.child.childFullName, reason: reasonOf(error) });
      }
      setProgress({ done: index + 1, total });
    }
    for (let index = 0; index < payerTargets.length; index += 1) {
      const child = payerTargets[index];
      try {
        await setPayer(kindergartenId, child.childId, child.parents[0].parentId);
        payersSet += 1;
      } catch (error) {
        failures.push({ child: child.childFullName, reason: reasonOf(error) });
      }
      setProgress({ done: assignable.length + index + 1, total });
    }

    setProgress(null);
    setReport({ assigned, total: assignable.length, payersSet, failures });
    if (assigned || payersSet) onDone();
  };

  const running = progress !== null;
  const loading = children === null && !failedToLoad;

  return (
    <Dialog open onClose={running ? undefined : onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 640, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600, pb: 0.5 }}>{t('contracts.assign.title')}</DialogTitle>
      <DialogContent>
        {failedToLoad ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {t('contracts.assign.loadFailed')}
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity={report.failures.length ? 'warning' : 'success'} sx={{ borderRadius: 2 }}>
              <div>{t('contracts.assign.reportAssigned', { done: report.assigned, total: report.total })}</div>
              {report.payersSet > 0 && <div>{t('contracts.assign.reportPayers', { count: report.payersSet })}</div>}
            </Alert>
            {report.failures.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('contracts.assign.reportFailed')}</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 14 }}>
                  {report.failures.map((failure, index) => (
                    <li key={`${failure.child}-${index}`}>{t('contracts.assign.failure', failure)}</li>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, color: palette.text.secondary, mb: 2.5 }}>
              {t('contracts.assign.description', { count: children!.length, kindergarten: kindergartenName ?? '' })}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <GenericTextField
                label={t('contracts.assign.pattern')}
                value={pattern}
                onChange={event => setPattern(event.target.value.trim())}
                error={!patternValid}
                helperText={patternValid ? t('contracts.assign.patternHint', { year, digits }) : t('contracts.assign.invalidPattern')}
                disabled={running}
                fullWidth
              />
              <GenericTextField
                label={t('contracts.assign.firstNumber')}
                value={firstNumber}
                onChange={event => {
                  setFirstTouched(true);
                  setFirstNumber(event.target.value.trim());
                }}
                error={!firstValid}
                helperText={
                  !firstValid
                    ? t('contracts.assign.invalidFirst')
                    : info?.lastUsedNumber
                      ? t('contracts.assign.lastUsed', { number: String(info.lastUsedNumber).padStart(digits, '0') })
                      : t('contracts.assign.noneUsed')
                }
                disabled={running}
                sx={{ width: 200, flexShrink: 0 }}
              />
            </Stack>

            {!assignable.length ? (
              <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                {t('contracts.assign.nobody')}
              </Alert>
            ) : null}

            {preview.length > 0 && (
              <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 2, maxHeight: 280, overflowY: 'auto', mb: 2 }}>
                <Table size="small" stickyHeader aria-label={t('contracts.assign.title')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('contracts.assign.columns.child')}</TableCell>
                      <TableCell>{t('contracts.assign.columns.group')}</TableCell>
                      <TableCell>{t('contracts.assign.columns.gets')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map(({ child, number }) => (
                      <TableRow key={child.childId}>
                        <TableCell>{child.childFullName}</TableCell>
                        <TableCell>{child.groupName ?? '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {child.membershipId ? (
                            number ?? '—'
                          ) : (
                            <Box component="span" sx={{ color: 'warning.main', fontWeight: 400 }}>
                              {t('contracts.assign.skipped')}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {payerCandidates.length > 0 && (
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={setPayers} onChange={event => setSetPayers(event.target.checked)} disabled={running} />}
                  label={<Typography sx={{ fontSize: 14 }}>{t('contracts.assign.setPayers')}</Typography>}
                />
                {setPayers && (
                  <Box component="ul" sx={{ m: 0, pl: 6, fontSize: 13, color: palette.text.secondary }}>
                    {payerCandidates.map(child => (
                      <li key={child.childId}>{t('contracts.assign.payerPair', { child: child.childFullName, parent: child.parents[0].fullName })}</li>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {running && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={progress!.total ? (progress!.done / progress!.total) * 100 : 100} />
                <Typography sx={{ fontSize: 12, color: palette.text.secondary, mt: 0.5 }}>{t('contracts.assign.running', progress!)}</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {report || failedToLoad ? (
          <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" label={t('common.close')} onClick={onClose} />
        ) : (
          <>
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} disabled={running} />
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              label={t('contracts.assign.submit', { count: assignable.length })}
              onAsyncAction={run}
              disabled={loading || running || !patternValid || !firstValid || (!assignable.length && !(setPayers && payerCandidates.length))}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignContractNumbersDialog;
